import {
  PERIOD_FROM,
  PERIOD_TO,
  PersonAnalysisPerson,
  Row,
  dateOf,
  isActivePeriod,
  isEmpty,
  text,
  todayIso,
} from './personAnalysis.types';

// Figures for the person view, read from the live rows the backend returns. Keys are the
// hasteService column ids (see personAnalysis.types.ts / person_analysis.rs).

const numberFormat = new Intl.NumberFormat('sv-SE', {
  maximumFractionDigits: 2,
});
const moneyFormat = new Intl.NumberFormat('sv-SE', {
  maximumFractionDigits: 0,
});

export const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value.replace(',', '.').replace(/\s/g, ''));
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

export const formatNumber = (value: unknown) => {
  const n = toNumber(value);
  return n === null ? text(value) : numberFormat.format(n);
};

export const formatMoney = (value: unknown, unit = 'kr') => {
  const n = toNumber(value);
  return n === null ? '' : `${moneyFormat.format(n)} ${unit}`;
};

export const formatPercent = (value: unknown) => {
  const n = toNumber(value);
  return n === null ? '' : `${numberFormat.format(n)} %`;
};

// Dates as yyyy-mm-dd, other values as text.
export const formatValue = (key: string, value: unknown) => {
  if (isEmpty(value)) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return dateOf(value);
  }
  if (typeof value === 'number') return numberFormat.format(value);
  if (typeof value === 'boolean') return value ? '✓' : '–';
  return text(value);
};

// The period active today, else the one that started last.
export const currentPeriod = (periods: Row[]): Row | null => {
  const today = todayIso();
  return (
    periods.find((p) => isActivePeriod(p, today)) ??
    [...periods].sort((a, b) =>
      dateOf(b[PERIOD_FROM]).localeCompare(dateOf(a[PERIOD_FROM]))
    )[0] ??
    null
  );
};

// First day of the earliest period (the employee's start).
export const employedSince = (periods: Row[]) =>
  periods
    .map((p) => dateOf(p[PERIOD_FROM]))
    .filter(Boolean)
    .sort()[0] ?? '';

// The org-unit objects (employmentPeriod.objects.<reference>) of a row, label text when there is one.
export const objectsOf = (row: Row | null) =>
  Object.keys(row ?? {})
    .filter(
      (key) =>
        key.startsWith('employmentPeriod.objects.') &&
        !key.endsWith('.desc') &&
        !key.endsWith('.fromDate')
    )
    .map((key) => ({
      key,
      reference: key.slice('employmentPeriod.objects.'.length),
      value: text(row?.[`${key}.desc`]) || text(row?.[key]),
    }))
    .filter((o) => o.value);

export interface SalaryChange {
  from: string;
  // Löneår set on the salary entry.
  year: number | null;
  previous: number | null;
  value: number;
  change: number | null;
  changePercent: number | null;
}

// The salary in effect over time (monthly, else hourly), oldest first, one entry per change.
export const salaryChanges = (history: Row[]): SalaryChange[] => {
  const key = history.some(
    (r) => toNumber(r['employmentPeriod.monthlySalary']) !== null
  )
    ? 'employmentPeriod.monthlySalary'
    : 'employmentPeriod.hourlySalary';

  const points = history
    .map((r) => ({
      from: dateOf(r['master.from']),
      value: toNumber(r[key]),
      year: toNumber(r[`${key}.year`]),
    }))
    .filter(
      (p): p is { from: string; value: number; year: number | null } =>
        Boolean(p.from) && p.value !== null
    )
    .sort((a, b) => a.from.localeCompare(b.from));

  return points.reduce<SalaryChange[]>((changes, point) => {
    const last = changes[changes.length - 1];
    if (last && last.value === point.value) return changes;
    const previous = last?.value ?? null;
    return [
      ...changes,
      {
        from: point.from,
        year: point.year,
        previous,
        value: point.value,
        change: previous === null ? null : point.value - previous,
        changePercent: previous
          ? ((point.value - previous) / previous) * 100
          : null,
      },
    ];
  }, []);
};

// A vacation figure by its column suffix, from `vacation_debt_days.*` first, then `vacation_debt.*`.
export const vacationValue = (vacation: Row | null, suffix: string) => {
  if (!vacation) return null;
  const days = vacation[`vacation_debt_days.${suffix}`];
  return isEmpty(days) ? vacation[`vacation_debt.${suffix}`] ?? null : days;
};

export interface PersonOverview {
  current: Row | null;
  active: boolean;
  employedSince: string;
  monthlySalary: number | null;
  hourlySalary: number | null;
  employmentRate: number | null;
  vacationDaysLeft: number | null;
  vacationEntitlement: number | null;
  vacationDebt: number | null;
  fte: number | null;
  ftePeriod: string;
  lastSalaryChange: SalaryChange | null;
  // Löneår of the current salary; the next review is taken as the year after (for now).
  salaryYear: number | null;
  nextReviewYear: number | null;
}

export const personOverview = (
  person: PersonAnalysisPerson
): PersonOverview => {
  const current = currentPeriod(person.periods);
  const changes = salaryChanges(person.history);
  const salaryYear =
    toNumber(current?.['employmentPeriod.monthlySalary.year']) ??
    toNumber(current?.['employmentPeriod.hourlySalary.year']) ??
    toNumber(current?.['employmentPeriod.timeSalary.year']);
  const paidLeft = toNumber(vacationValue(person.vacation, 'Betald_Antal'));
  const unpaidLeft = toNumber(vacationValue(person.vacation, 'Obetalda_Antal'));

  return {
    current,
    active: current ? isActivePeriod(current) : false,
    employedSince: employedSince(person.periods),
    monthlySalary: toNumber(current?.['employmentPeriod.monthlySalary']),
    hourlySalary: toNumber(current?.['employmentPeriod.hourlySalary']),
    employmentRate: toNumber(current?.['employmentPeriod.employmentRate']),
    vacationDaysLeft:
      paidLeft === null && unpaidLeft === null
        ? null
        : (paidLeft ?? 0) + (unpaidLeft ?? 0),
    vacationEntitlement:
      toNumber(vacationValue(person.vacation, '_Semesterrätt')) ??
      toNumber(current?.['person.annualLeave']),
    vacationDebt: toNumber(vacationValue(person.vacation, 'Total_Belopp')),
    fte: toNumber(person.fte?.['kpi_employment.FTE']),
    ftePeriod: text(person.fte?.['kpi_employment.period']),
    lastSalaryChange: changes.length > 1 ? changes[changes.length - 1] : null,
    salaryYear,
    nextReviewYear: salaryYear === null ? null : salaryYear + 1,
  };
};

// Periods newest first.
export const sortedPeriods = (periods: Row[]) =>
  [...periods].sort(
    (a, b) =>
      dateOf(b[PERIOD_FROM]).localeCompare(dateOf(a[PERIOD_FROM])) ||
      dateOf(b[PERIOD_TO] ?? '9999').localeCompare(
        dateOf(a[PERIOD_TO] ?? '9999')
      )
  );

export type TimeKind = 'absence' | 'attendance' | 'other';

// Absence: paycodes 4xxxx. Attendance: "Arbetad tid"/"Arbetade timmar" (by description), or
// paycodes 1xxxx except 11xxx.
export const timeKind = (row: Row): TimeKind => {
  const paycode = text(row['timeTransaction.paycode']).trim();
  const description = text(row['timeTransaction.paycode.desc'])
    .toLowerCase()
    .replace(/\s/g, '');
  if (paycode.startsWith('4')) return 'absence';
  if (
    description.startsWith('arbetad') ||
    (paycode.startsWith('1') && !paycode.startsWith('11'))
  ) {
    return 'attendance';
  }
  return 'other';
};

// A comment written on the same day as a change, to show next to it.
export const commentOn = (comments: Row[], date: string) =>
  text(
    comments.find((c) => dateOf(c['comment.created']) === date)?.[
      'comment.comment'
    ]
  );

// ---- Semesterskuld: the debt report per month (as Aqord's "Semester - Underlag") ----

const DEBT_PREFIX = 'vacation_debt.';
// Columns of a debt row that aren't report figures.
const DEBT_INFO_COLUMNS = new Set(['period', 'vacation_rule', 'workplace']);
// Aqord's row order ("Semester - Underlag"); other groups go before the totals.
const DEBT_LINE_ORDER = [
  'Intjänad',
  'Uttagen',
  'Betald',
  'Sparad',
  'Förskott',
  'Förfallen',
  'Total',
  'Total soc',
];
const debtLineRank = (label: string) => {
  const exact = DEBT_LINE_ORDER.indexOf(label);
  if (exact >= 0) return exact;
  // "Förfallen (intjänade)" etc. with Förfallen
  const prefix = DEBT_LINE_ORDER.findIndex((l) => label.startsWith(`${l} `));
  return prefix >= 0 && !label.startsWith('Total')
    ? prefix
    : DEBT_LINE_ORDER.indexOf('Total') - 0.5;
};

// Report columns holding a number of days; the others are amounts.
const DEBT_DAY_COLUMNS = new Set(['Antal', 'Dagar']);

export interface DebtMonthCell {
  days: number | null;
  amount: number | null;
}

export interface DebtLine {
  // The report group ("Intjänad", "Sparad", ...; "Total soc" for the social fees).
  label: string;
  total: boolean;
  // By period (YYYYMM).
  months: Record<string, DebtMonthCell>;
  // Last month minus the first.
  change: DebtMonthCell;
}

export interface DebtMonths {
  periods: string[];
  lines: DebtLine[];
  vacationRule: string;
}

// "vacation_debt.Intjänad_Antal" -> ["Intjänad", "Antal"]; null for the info columns.
const debtColumn = (key: string): [string, string] | null => {
  if (!key.startsWith(DEBT_PREFIX) || key.endsWith('.desc')) return null;
  const name = key.slice(DEBT_PREFIX.length);
  const split = name.lastIndexOf('_');
  if (DEBT_INFO_COLUMNS.has(name) || split < 0) return null;
  return [name.slice(0, split), name.slice(split + 1)];
};

const difference = (last: number | null, first: number | null) =>
  last === null && first === null ? null : (last ?? 0) - (first ?? 0);

// The debt rows (one per month) as one line per report group with a days and an amount
// figure per month, in Aqord's order; Total's "Soc" gets its own line.
export const debtMonths = (rows: Row[]): DebtMonths => {
  const periods = rows.map((r) => text(r[`${DEBT_PREFIX}period`]));
  const lines = new Map<string, DebtLine>();
  const lineOf = (label: string) => {
    let line = lines.get(label);
    if (!line) {
      line = {
        label,
        total: label.startsWith('Total'),
        months: {},
        change: { days: null, amount: null },
      };
      lines.set(label, line);
    }
    return line;
  };

  rows.forEach((row, i) => {
    const period = periods[i];
    Object.entries(row).forEach(([key, value]) => {
      const column = debtColumn(key);
      const n = toNumber(value);
      if (!column || n === null) return;
      const [group, name] = column;
      const line = lineOf(group === 'Total' && name === 'Soc' ? 'Total soc' : group);
      const cell = line.months[period] ?? { days: null, amount: null };
      line.months[period] = cell;
      if (DEBT_DAY_COLUMNS.has(name)) cell.days = n;
      else cell.amount = n;
    });
  });

  const first = periods[0];
  const last = periods[periods.length - 1];
  lines.forEach((line) => {
    line.change = {
      days: difference(
        line.months[last]?.days ?? null,
        line.months[first]?.days ?? null
      ),
      amount: difference(
        line.months[last]?.amount ?? null,
        line.months[first]?.amount ?? null
      ),
    };
  });

  const latest = rows[rows.length - 1];
  return {
    periods,
    lines: Array.from(lines.values()).sort(
      (a, b) => debtLineRank(a.label) - debtLineRank(b.label)
    ),
    vacationRule:
      text(latest?.[`${DEBT_PREFIX}vacation_rule.desc`]) ||
      text(latest?.[`${DEBT_PREFIX}vacation_rule`]),
  };
};

// "202608" -> "2026-08"
export const periodLabel = (period: string) =>
  /^\d{6}$/.test(period) ? `${period.slice(0, 4)}-${period.slice(4)}` : period;

// ---- Semester: vacation days per vacation year (HR mode, days only) ----

// A day figure of the HR report (`vacation_debt_days.*`); never the amount columns.
export const dayValue = (vacation: Row | null, suffix: string) =>
  toNumber(vacation?.[`vacation_debt_days.${suffix}`]);

export interface VacationYear {
  // "2026/2027"
  label: string;
  // Years back: 0 = the current vacation year, N = saved year N.
  back: number;
  entitlement: number | null;
  earned: number | null;
  taken: number | null;
  remaining: number;
}

export interface VacationDays {
  entitlement: number | null;
  earned: number | null;
  saved: number;
  // This year's days left (paid + unpaid).
  currentRemaining: number;
  available: number;
  advance: number | null;
  advanceTaken: number | null;
  // The current year first, then the saved years that have days, newest first.
  years: VacationYear[];
  // The oldest saved year and the year its days must be taken by.
  oldestSaved: { label: string; takeBy: string } | null;
}

// The month a vacation year starts in, from the vacation rule's code (Aqord's
// VacationRulesSet: ProcRuleJan, SameRuleAprCurr, SameRuleJunSem, ...). April
// (Semesterlagen's default) when the rule names none.
export const vacationYearStartMonth = (rule: unknown) => {
  const code = text(rule);
  if (/Jan|Dir/.test(code)) return 1;
  if (/Jun/.test(code)) return 6;
  if (/Jul/.test(code)) return 7;
  return 4;
};

// "2026" for calendar years, "2026/2027" for years spanning two.
const yearLabel = (startYear: number, startMonth: number) =>
  startMonth === 1 ? `${startYear}` : `${startYear}/${startYear + 1}`;

// Start year of the vacation year a date falls in.
export const vacationYearStart = (date: string, startMonth: number) => {
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7));
  return month >= startMonth ? year : year - 1;
};

// Vacation years start in the month the vacation rule names.
// Saved year N (paycodes 0321x-0325x, "spar år N") is the vacation year N years back.
export const vacationDays = (
  vacation: Row | null,
  paymentDate: string | null,
  fallbackEntitlement: number | null
): VacationDays => {
  const startMonth = vacationYearStartMonth(
    vacation?.['vacation_debt_days.vacation_rule']
  );
  const start = vacationYearStart(dateOf(paymentDate) || todayIso(), startMonth);
  const entitlement = dayValue(vacation, '_Semesterrätt') ?? fallbackEntitlement;
  const earned = dayValue(vacation, 'Intjänad_Antal');
  const taken = dayValue(vacation, '_Uttagna (Semesterårets)');
  const currentRemaining =
    (dayValue(vacation, 'Betald_Antal') ?? 0) +
    (dayValue(vacation, 'Obetalda_Antal') ?? 0);

  const savedYears: VacationYear[] = [1, 2, 3, 4, 5]
    .map((back) => {
      const days = dayValue(vacation, `Sparad_År ${back}`) ?? 0;
      return {
        label: yearLabel(start - back, startMonth),
        back,
        entitlement: days,
        earned: null,
        taken: null,
        remaining: days,
      };
    })
    .filter((y) => y.remaining !== 0);
  const saved =
    dayValue(vacation, 'Sparad_Total') ??
    savedYears.reduce((sum, y) => sum + y.remaining, 0);
  const oldest = savedYears[savedYears.length - 1];

  return {
    entitlement,
    earned,
    saved,
    currentRemaining,
    available: currentRemaining + saved,
    advance: dayValue(vacation, '_Förskottsdagar'),
    advanceTaken: dayValue(vacation, '_Förskottsdagar uttagna'),
    years: [
      {
        label: yearLabel(start, startMonth),
        back: 0,
        entitlement,
        earned,
        taken,
        remaining: currentRemaining,
      },
      ...savedYears,
    ],
    oldestSaved: oldest
      ? {
          label: oldest.label,
          takeBy: yearLabel(start - oldest.back + 5, startMonth),
        }
      : null,
  };
};
